/**
 * Attractions as Feather's backend serves them (FE2-T168).
 *
 * Ported 1:1 from the Android `AttractionModels.kt`. The wire shape is Feather's own (it
 * normalizes Viator and any future provider before serving), so nothing here should ever grow a
 * `viator` in its name. Endpoints: `api/v3/shops/{shopId}/attractions[/recommended | /{code}]`.
 *
 * NAMING: keys are snake_case, matching the JSON exactly (the web repo maps no field names).
 * NULLABILITY: every field is OPTIONAL — a details response is a strict superset of a list item, so
 * the fields under "details only" are simply absent on list/recommended items. Absent is not an
 * error: a list card has no use for an itinerary.
 */

/**
 * The envelope both collection endpoints return: the list and `/recommended`.
 *
 * `total_count` is the size of the WHOLE catalogue the backend serves (capped server-side), not this
 * page — it drives load-more. On `/recommended`, which is not paginated, it equals `items.length`.
 */
export interface AttractionsResponse {
  items?: Attraction[];
  total_count?: number;
}

/**
 * One attraction — a single superset interface for the list, `/recommended`, and the details screen.
 */
export interface Attraction {
  /** Always "viator" today. Present so a second provider needs no client change. */
  provider?: string;
  /** The id for the details endpoint. Also the stable list key. */
  provider_product_id?: string;
  title?: string;
  description?: string;

  /** Floating-point: the backend passes the provider's number through untouched (10 and 10.5 occur). */
  starting_price?: number;
  /** Present, and higher than starting_price, only when discounted. Drives the struck-through price. */
  price_before_discount?: number;
  currency?: string;

  /** Already rounded to 1dp server-side, so every client shows the same number. */
  rating?: number;
  review_count?: number;

  /** Fixed duration, or the upper bound of a variable-length tour. */
  duration_minutes?: number;

  /**
   * Affiliate deep link carrying Feather's tracking. Open exactly as given — rebuilding or
   * stripping it discards the commission, which is the entire revenue path for this feature.
   */
  booking_url?: string;

  /** Largest variant of each image, cover first. Empty when the attraction has none. */
  image_urls?: string[];
  tags?: number[];
  /** e.g. "FREE_CANCELLATION", "LIKELY_TO_SELL_OUT". */
  flags?: string[];

  /** The venue picked this. Badge it; do not re-sort on it — order is server-owned. */
  is_recommended?: boolean;
  /** The banner one. At most one per venue. */
  is_highlighted?: boolean;
  /** The admin's ordering. Null when not recommended. Already applied server-side. */
  position?: number;

  /**
   * The language the content IS in, which is not necessarily the one requested — the provider does
   * not carry every language Feather supports.
   */
  content_language?: string;
  is_machine_translated?: boolean;

  // ---- details only: absent unless this came from the details endpoint ----

  /** Per-star counts, keys "1".."5". Always all five when present. */
  rating_histogram?: { [star: string]: number };
  /** Per-provider split, e.g. VIATOR 41 @ 4.7 + TRIPADVISOR 62 @ 4.6. */
  review_sources?: AttractionReviewSource[];
  supplier_name?: string;
  language_guides?: AttractionLanguageGuide[];
  private_tour?: boolean;
  skip_the_line?: boolean;
  max_travelers_in_shared_tour?: number;
  itinerary?: AttractionItineraryStop[];
  /** Objects, not strings — the provider ships a category/type/description triple. */
  inclusions?: AttractionInclusion[];
  exclusions?: AttractionInclusion[];
  cancellation_policy?: AttractionCancellationPolicy;
  meeting_point?: AttractionMeetingPoint;
  /** e.g. "MEET_EVERYONE_AT_START_POINT". */
  traveler_pickup?: string;
  ticket_info?: AttractionTicketInfo;
  additional_info?: AttractionAdditionalInfo[];
  booking_requirements?: AttractionBookingRequirements;
  age_bands?: AttractionAgeBand[];
  /** e.g. "PER_PERSON". */
  pricing_type?: string;
}

export interface AttractionReviewSource {
  provider?: string;
  total_count?: number;
  average_rating?: number;
}

/** type: delivery format — GUIDE, AUDIO, WRITTEN. A written-only language is not a live guide. */
export interface AttractionLanguageGuide {
  type?: string;
  language?: string;
}

export interface AttractionInclusion {
  category?: string;
  type?: string;
  description?: string;
}

export interface AttractionItineraryStop {
  description?: string;
  pass_by_without_stopping?: boolean;
  admission_included?: string;
  attraction_id?: number;
  location?: AttractionLocation;
}

/** A place on the map — or not. Coordinates are nullable everywhere; render the description when absent. */
export interface AttractionLocation {
  name?: string;
  address?: AttractionAddress;
  lat?: number;
  lng?: number;
  has_coordinates?: boolean;
}

export interface AttractionAddress {
  street?: string;
  city?: string;
  country?: string;
}

export interface AttractionMeetingPoint {
  description?: string;
  location?: AttractionLocation;
}

export interface AttractionCancellationPolicy {
  type?: string;
  description?: string;
  cancel_if_bad_weather?: boolean;
  cancel_if_insufficient_travelers?: boolean;
  refund_eligibility?: AttractionRefundEligibility[];
}

export interface AttractionRefundEligibility {
  day_range_min?: number;
  day_range_max?: number;
  percentage_refundable?: number;
}

export interface AttractionTicketInfo {
  types?: string[];
  description?: string;
  per_booking?: string;
}

export interface AttractionAdditionalInfo {
  /** Typed accessibility / logistics flag — STROLLER_ACCESSIBLE, PETS_WELCOME, ... */
  type?: string;
  description?: string;
}

export interface AttractionBookingRequirements {
  min_travelers?: number;
  max_travelers?: number;
  requires_adult?: boolean;
}

export interface AttractionAgeBand {
  band?: string;
  start_age?: number;
  end_age?: number;
}

/** Flag values carried in `Attraction.flags`. */
export const FLAG_FREE_CANCELLATION = 'FREE_CANCELLATION';
export const FLAG_LIKELY_TO_SELL_OUT = 'LIKELY_TO_SELL_OUT';

/** `traveler_pickup` value meaning "meet at the start point" rather than a real pickup. */
export const PICKUP_MEET_AT_START = 'MEET_EVERYONE_AT_START_POINT';

/** The server caps a page at 50. Asking for more is silently clamped, not an error. */
export const ATTRACTIONS_PAGE_SIZE = 50;

/**
 * Master switch for the "from" price on the attractions list screen (cards + carousel tiles).
 * false hides it across TouristAttractionsScreen; the details sheet keeps its own price.
 */
export const SHOW_ATTRACTION_PRICE = false;
